<script setup lang="ts">
    import type { SbBlokData } from '@storyblok/js';
    import sidebarClose from '~/assets/icons/close.svg';
    import sidebarOpen from '~/assets/icons/menu.svg';
    import type { FachkreisRequiredField } from '~/types/censhare/fachkreisValidation';
    import type { ProductItemGroup, Usps } from '~/types/censhare/productItemGroup';
    import type { PublicCenshareConfig } from '~/utils/CenshareClient/CenshareClient';
    import { CENSHARE_REQUIRED_FIELDS_MISSING } from '~/utils/CenshareClient/constants';
    import { extractStatusCode } from '~/utils/extractStatusCodeFromError';
    import { filterLocalizedContent, findLocalizedContent } from '~/utils/findLocalizedCenshareContent';
    import { sanitizeHtml } from '~/utils/sanitizeHtml';

    const route = useRoute();

    const idOrSlug = computed(() => route.path.split('/').findLast(Boolean));
    if (!idOrSlug.value) {
        throw createError({ status: 404, statusMessage: 'Product ID missing or undefined' });
    }

    const publicCenshareConfig = useRuntimeConfig().public.censhare as PublicCenshareConfig;
    const censhareEndpoint = publicCenshareConfig.endpoint;

    const { country, lang } = useLanguage();
    const { t } = useI18n();
    const { isDraftModeEnabled } = useDraftMode();

    type FileType = 'pdf' | 'jpeg' | 'png' | 'doc' | 'oth';

    const { getDownloadName } = useGetDownloadName();

    definePageMeta({ ssr: true });

    type Tab = {
        title: string;
        isLocked?: boolean | undefined;
    };

    type FootnoteValue = string | string[] | undefined;

    interface Footnote {
        number: number;
        text: string;
    }

    interface ProductUspFeaturesData {
        slogan: string;
        uspFeatures: string[];
        footnotes: Footnote[];
        sloganFootnoteNumbers: number[];
        featureFootnoteNumbers: number[][];
        footnoteIndex?: number;
    }

    const headers = useRequestHeaders(['cookie']);

    // fetch cenchare product data from our backend
    const { data, error: errorCenshare } = await useFetch<ProductItemGroup>(
        `/api/censhare/productitemgroup/${idOrSlug.value}`,
        {
            headers,
            query: {
                country: country.value,
                lang: lang.value
            }
        }
    );

    type CenshareValidationErrorData = {
        code?: string;
        missingFields?: FachkreisRequiredField[];
    };

    const censhareValidationErrorData = computed(() => {
        if (!isDraftModeEnabled.value) {
            return null;
        }

        const errorData = (errorCenshare.value?.data as { data?: CenshareValidationErrorData })?.data;
        if (errorData?.code !== CENSHARE_REQUIRED_FIELDS_MISSING || !errorData.missingFields?.length) {
            return null;
        }

        return errorData;
    });

    const shouldShowDraftValidation = computed(
        () => isDraftModeEnabled.value && Boolean(censhareValidationErrorData.value)
    );

    const censhareStatusCode = computed(() => extractStatusCode(errorCenshare.value));

    const hasRenderableProductData = computed(() => {
        const product = data.value;
        if (!product) {
            return false;
        }

        return Boolean(
            product.id ||
            product.name ||
            product.nameLocalized ||
            (Array.isArray(product.productItems) && product.productItems.length > 0)
        );
    });

    const buildCensharePageError = (fallbackStatusCode: number) => {
        const statusCode = censhareStatusCode.value;

        return createError({
            ...errorCenshare.value,
            statusCode: typeof statusCode === 'number' ? statusCode : fallbackStatusCode,
            statusMessage: errorCenshare.value?.statusMessage || 'Product not found',
            fatal: true
        });
    };

    const raisePageError = (error: ReturnType<typeof createError>) => {
        if (import.meta.client) {
            showError(error);
        }

        throw error;
    };

    const shouldRaiseProductError = computed(() => errorCenshare.value && !shouldShowDraftValidation.value);

    const shouldRaiseNoDataError = computed(
        () => !hasRenderableProductData.value && !censhareValidationErrorData.value
    );

    watchEffect(() => {
        if (shouldRaiseProductError.value) {
            const statusCode = censhareStatusCode.value;
            if (typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500 && import.meta.server) {
                const event = useRequestEvent();
                if (event) {
                    setResponseStatus(event, statusCode, errorCenshare.value.statusMessage || 'Product not found');
                }
            }
            raisePageError(buildCensharePageError(404));
        }

        if (shouldRaiseNoDataError.value) {
            const statusCode = censhareStatusCode.value;
            if (import.meta.server) {
                const event = useRequestEvent();
                if (event) {
                    setResponseStatus(
                        event,
                        typeof statusCode === 'number' ? statusCode : 404,
                        errorCenshare.value?.statusMessage || 'Product not found'
                    );
                }
            }
            raisePageError(buildCensharePageError(404));
        }
    });

    const { rawPath } = useCleanStoryblokPath();
    const { data: story, error } = await useStory<FachkreisPDPStoryblok>(rawPath);

    const canonicalLink = computed(() => {
        return story.value?.content?.canonicalLink;
    });

    const canonicalUrlAbsolute = useCanonicalUrl(canonicalLink);

    useHead(() => ({
        link: [{ rel: 'canonical', href: canonicalUrlAbsolute.value }]
    }));

    if (error?.value) {
        console.error(error.value);
    }

    const loginModalController = useModal();

    const { isLoggedIn } = useLoginState();
    const isLoggedInClient = useClientGate(isLoggedIn);

    const parseXml = (xmlString: string) => {
        return xmlString
            .replaceAll('<title>', '<h2>')
            .replaceAll('</title>', '</h2>')
            .replaceAll('<subtitle>', '<h3>')
            .replaceAll('</subtitle>', '</h3>')
            .replaceAll('<text>', '<div>')
            .replaceAll('</text>', '</div>')
            .replaceAll('<paragraph>', '<p>')
            .replaceAll('</paragraph>', '</p>')
            .replaceAll('<bold>', '<strong>')
            .replaceAll('</bold>', '</strong>');
    };

    const sortedPZN = computed(() => (data?.value?.productItems ? [...data.value.productItems] : []));

    const hasProductItems = computed(() => sortedPZN.value.length > 0);

    // order and create tabs
    const tabs = computed<Tab[]>(() => [
        { title: t('product.tabs.information'), isLocked: false },
        ...(hasProductItems.value
            ? [{ title: t('product.tabs.package', sortedPZN.value.length), isLocked: false }]
            : []),
        { title: t('product.tabs.downloads'), isLocked: false }
    ]);

    const campaignsTag = t('product.tabs.campaigns');
    const faqTag = t('product.tabs.faq');
    const trainingsTag = t('product.tabs.trainings');
    const studiesTag = t('product.tabs.studies');

    // updates or adds Tabs if isLoggedIn value changes
    const addOrUpdateTab = (array: Tab[], newTab: Tab) => {
        const index = array.findIndex((tab) => tab.title === newTab.title);
        if (index === -1) {
            array.push(newTab);
        } else {
            array[index] = { ...array[index], ...newTab };
        }
    };

    const allTabs = computed(() => {
        const storyblokTabs = [...tabs.value];

        if (story.value?.content?.ConditionsAndCampaigns?.length)
            addOrUpdateTab(storyblokTabs, { title: campaignsTag, isLocked: false });

        if (story.value?.content?.faq?.length || story.value?.removedContents?.includes('faq'))
            addOrUpdateTab(storyblokTabs, { title: faqTag, isLocked: !isLoggedInClient.value });

        if (story.value?.content?.trainings?.length || story.value?.removedContents?.includes('trainings'))
            addOrUpdateTab(storyblokTabs, { title: trainingsTag, isLocked: !isLoggedInClient.value });

        if (story.value?.content?.studies?.length || story.value?.removedContents?.includes('studies'))
            addOrUpdateTab(storyblokTabs, { title: studiesTag, isLocked: !isLoggedInClient.value });

        return storyblokTabs;
    });

    const { width } = useWindowSize();
    const windowWidth = ref();

    const activeTab = ref(tabs.value[0]?.title ?? '');
    const tabsWrapper = ref(null);

    const setActiveTab = (tab: Tab) => {
        if (tab.isLocked) {
            loginModalController.open();
            return;
        }
        activeTab.value = tab.title;
        // make sure everything is up and ready in the DOM
        if (windowWidth.value < 768) {
            nextTick(() => {
                const tabContent = document.getElementById('tab-content');
                tabContent?.scrollIntoView({ behavior: 'smooth' });
            });
        }
    };

    const productInformation = computed(() => {
        const baseData = findLocalizedContent(data.value?.mainContents, 'text.basedata.', lang.value);
        return baseData?.copytext?.split('</productDescriptionShort>')?.[0];
    });

    const productMandatoryTextsHcp = computed(() =>
        filterLocalizedContent(data.value?.mainContents, 'text.mandatorytext.hcp.', lang.value)
    );
    const productMandatoryTextsEc = computed(() =>
        filterLocalizedContent(data.value?.mainContents, 'text.mandatorytext.ec.', lang.value)
    );

    const productUsps = computed(() => data.value?.mainContents?.filter((content) => content.usps));

    const addFootnotesFromValue = (
        footnoteValue: FootnoteValue,
        startIndex: number,
        targetFootnotes: { number: number; text: string }[],
        numbersCollector?: number[]
    ) => {
        let index = startIndex;
        if (!footnoteValue) return index;

        const values = Array.isArray(footnoteValue) ? footnoteValue : [footnoteValue];
        for (const raw of values) {
            const trimmed = raw.trim();
            if (!trimmed.length) continue;

            targetFootnotes.push({ number: index, text: trimmed });
            if (numbersCollector) numbersCollector.push(index);
            index++;
        }

        return index;
    };

    const productUspFeatures = computed<ProductUspFeaturesData>(() => {
        let slogan = '';
        const uspFeatures: string[] = [];

        const footnotes: { number: number; text: string }[] = [];
        const sloganFootnoteNumbers: number[] = [];
        const featureFootnoteNumbers: number[][] = [];

        if (!productUsps.value || productUsps.value.length <= 0) {
            return { slogan, uspFeatures, footnotes, sloganFootnoteNumbers, featureFootnoteNumbers };
        }

        const usps = productUsps.value?.[0]?.usps as Usps | undefined;
        if (!usps || !usps.features) {
            return { slogan, uspFeatures, footnotes, sloganFootnoteNumbers, featureFootnoteNumbers };
        }

        let footnoteIndex = 1;

        if (usps.productSlogan) {
            if (typeof usps.productSlogan === 'string') {
                slogan = usps.productSlogan.trim();
            } else if (usps.productSlogan['#text'] && typeof usps.productSlogan['#text'] === 'string') {
                slogan = usps.productSlogan['#text'].trim();
                footnoteIndex = addFootnotesFromValue(
                    (usps.productSlogan as Record<string, unknown>).footnote as FootnoteValue,
                    footnoteIndex,
                    footnotes,
                    sloganFootnoteNumbers
                );
            }
        }

        for (const feature of usps.features) {
            const featureFootnotes: number[] = [];

            footnoteIndex = addFootnotesFromValue(
                feature.footnote as FootnoteValue,
                footnoteIndex,
                footnotes,
                featureFootnotes
            );

            featureFootnoteNumbers.push(featureFootnotes);

            if (feature.text) uspFeatures.push(feature.text.trim());
        }

        if (usps.productOtherUsps) {
            uspFeatures.push(usps.productOtherUsps.trim());
        }

        return { slogan, uspFeatures, footnotes, sloganFootnoteNumbers, featureFootnoteNumbers, footnoteIndex };
    });

    const productInformationFootnoteTexts = computed(() => {
        const xml = productInformation.value;
        if (!xml) return [];

        const texts: string[] = [];
        const matches = xml.match(/<footnote>([\s\S]*?)<\/footnote>/g);
        if (matches) {
            matches.forEach((match: string) => {
                const text = match.replace(/<footnote>|<\/footnote>/g, '').trim();
                if (text) texts.push(text);
            });
        }
        return texts;
    });

    const footnoteTextToNumber = computed(() => {
        const footnoteNumberMap = new Map<string, number>();
        const seen = new Set<string>();
        let number = 1;

        for (const fn of productUspFeatures.value.footnotes) {
            const key = fn.text.toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                footnoteNumberMap.set(key, number);
                number++;
            }
        }

        for (const text of productInformationFootnoteTexts.value) {
            const key = text.toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                footnoteNumberMap.set(key, number);
                number++;
            }
        }

        return footnoteNumberMap;
    });

    const sloganFootnoteNumbersRemapped = computed(() => {
        return productUspFeatures.value.sloganFootnoteNumbers.map((oldNum: number) => {
            const footnote = productUspFeatures.value.footnotes.find((fn) => fn.number === oldNum);
            if (!footnote) return oldNum;
            return footnoteTextToNumber.value.get(footnote.text.toLowerCase()) ?? oldNum;
        });
    });

    const featureFootnoteNumbersRemapped = computed(() => {
        return productUspFeatures.value.featureFootnoteNumbers.map((oldNumArray: number[]) => {
            return oldNumArray.map((oldNum: number) => {
                const footnote = productUspFeatures.value.footnotes.find((fn) => fn.number === oldNum);
                if (!footnote) return oldNum;
                return footnoteTextToNumber.value.get(footnote.text.toLowerCase()) ?? oldNum;
            });
        });
    });

    const allFootnotes = computed(() => {
        const footnoteMap = new Map(footnoteTextToNumber.value);
        const result: { number: number; text: string }[] = [];
        const seenTexts = new Set<string>();

        // Add all USP footnotes
        for (const fn of productUspFeatures.value.footnotes) {
            const key = fn.text.toLowerCase();
            if (!seenTexts.has(key)) {
                const num = footnoteMap.get(key);
                if (num !== undefined) {
                    result.push({ number: num, text: fn.text });
                    seenTexts.add(key);
                }
            }
        }

        // Add all product information footnotes
        for (const text of productInformationFootnoteTexts.value) {
            const key = text.toLowerCase();
            if (!seenTexts.has(key)) {
                const num = footnoteMap.get(key);
                if (num !== undefined) {
                    result.push({ number: num, text });
                    seenTexts.add(key);
                }
            }
        }

        // Sort by number
        return result.sort((a, b) => a.number - b.number);
    });

    const productInformationHtml = computed(() => {
        const xml = productInformation.value;
        if (!xml) {
            return '';
        }

        const replaced = xml.replace(/<footnote>([\s\S]*?)<\/footnote>/g, (_, raw: string) => {
            const trimmed = raw.trim();
            if (!trimmed.length) {
                return '';
            }

            // Get the number for this footnote from the map (case insensitive)
            const number = footnoteTextToNumber.value.get(trimmed.toLowerCase());
            if (number === undefined) {
                return '';
            }

            const label = `<sup class="footnote-label"><a href="#footnote-${number}" class="footnote-link">${number}</a></sup>`;
            return label;
        });

        return sanitizeHtml(parseXml(replaced));
    });

    const showSidebar = ref(false);

    const handleSidebar = () => {
        showSidebar.value = !showSidebar.value;
    };

    const handleTabsScroll = (hash?: string) => {
        if (!hash) return;
        window.location.hash = hash;

        const offset = 100;
        const cleanHash = decodeURI(hash).replace('#', '');
        const y = (tabsWrapper.value as HTMLElement | null)?.getBoundingClientRect().top ?? 0;
        window.scrollTo({ top: y + window.scrollY - offset, behavior: 'smooth' });
        activeTab.value = cleanHash;
    };

    // simplest solution for the sideEffects anchor link, as discussed with our PO's
    const scrollToBottom = () => {
        window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth'
        });
    };

    onMounted(() => {
        windowWidth.value = width.value;
        const cleanHash = decodeURI(route.hash).replace('#', '');

        if (data?.value?.productItems) {
            sortedPZN.value.sort((a, b) => {
                return Number(a.pzn) - Number(b.pzn);
            });
        }

        if (tabs.value.map((tab) => tab.title).includes(cleanHash)) {
            handleTabsScroll(route.hash);
        }

        if (windowWidth.value > 768) {
            showSidebar.value = true;
        }
    });

    const bloks = computed(() => {
        return {
            conditionsAndCampaignsBloks: (story.value?.content?.ConditionsAndCampaigns || []).filter(
                (blok) => blok?.component
            ),
            faqBlok: story.value?.content?.faq?.[0]?.component ? story.value.content.faq[0] : null,
            trainingBloks: (story.value?.content?.trainings || []).filter(
                (blok: unknown) => (blok as Record<string, unknown>)?.component
            ),
            studyBloks: (story.value?.content?.studies || []).filter((blok) => blok?.component),
            calculatorBlok: story.value?.content?.calculator?.[0]?.component ? story.value.content.calculator[0] : null,
            associatedNewsBlok: story.value?.content?.AssociatedNews?.[0]?.component
                ? story.value.content.AssociatedNews[0]
                : null,
            additionalContent: story.value?.content?.additionalContent?.[0]?.component
                ? story.value.content.additionalContent[0]
                : null
        };
    });
</script>

<template>
    <div class="pdp">
        <div
            v-if="data || censhareValidationErrorData"
            class="max-width"
        >
            <StickyWrapper />
            <CenshareValidationMessage
                v-if="censhareValidationErrorData"
                :missing-fields="censhareValidationErrorData.missingFields ?? []"
            />

            <template v-else-if="data">
                <div class="product-grid --sidebar">
                    <div
                        class="product-gallery"
                        :class="{ '--single-image': data.media?.specialistGroupGallery?.length === 1 }"
                    >
                        <ClientOnly>
                            <ProductGallery :fachkreis-product="data" />
                        </ClientOnly>
                    </div>
                    <div class="product-description">
                        <div class="title">
                            <h1>{{ data.nameLocalized?.[lang] ?? data.name }}</h1>
                        </div>

                        <p class="claim">
                            {{ productUspFeatures.slogan }}
                            <sup
                                v-if="sloganFootnoteNumbersRemapped.length"
                                class="footnote-label"
                            >
                                <template
                                    v-for="(n, idx) in sloganFootnoteNumbersRemapped"
                                    :key="`slogan-fn-${n}`"
                                >
                                    <a
                                        :href="`#footnote-${n}`"
                                        class="footnote-link"
                                    >
                                        {{ n }}
                                    </a>
                                    <span v-if="(idx as number) < sloganFootnoteNumbersRemapped.length - 1"
                                        >,&thinsp;</span
                                    >
                                </template>
                            </sup>
                        </p>

                        <div
                            v-for="text in productUsps"
                            :key="text.id"
                        >
                            <ul
                                v-if="text.copytext"
                                class="usps"
                            >
                                <li
                                    v-for="(usp, index) in productUspFeatures.uspFeatures"
                                    :key="usp"
                                >
                                    <img
                                        src="~/assets/icons/check.svg"
                                        alt="checkmark-icon"
                                        aria-hidden="true"
                                    />
                                    <span>
                                        {{ usp }}
                                        <sup
                                            v-if="(featureFootnoteNumbersRemapped[index] ?? []).length > 0"
                                            class="footnote-label"
                                        >
                                            <template
                                                v-for="(n, idx) in featureFootnoteNumbersRemapped[index] ?? []"
                                                :key="`usp-fn-${index}-${n}`"
                                            >
                                                <a
                                                    :href="`#footnote-${n}`"
                                                    class="footnote-link"
                                                >
                                                    {{ n }}
                                                </a>
                                                <span
                                                    v-if="
                                                        (idx as number) <
                                                        (featureFootnoteNumbersRemapped[index] ?? []).length - 1
                                                    "
                                                    >,&thinsp;</span
                                                >
                                            </template>
                                        </sup>
                                    </span>
                                </li>
                            </ul>
                        </div>
                        <div
                            v-if="productInformation"
                            class="description margin"
                            data-testid="fachportal-product-description"
                        >
                            <h2>{{ t('product.description') }}</h2>
                            <!-- eslint-disable-next-line vue/no-v-html -->
                            <div v-html="productInformationHtml"></div>
                        </div>
                    </div>
                    <div class="sidebar-wrapper">
                        <div class="sidebar">
                            <div class="title">
                                <button
                                    :class="{ '--closed': !showSidebar }"
                                    @click="handleSidebar"
                                >
                                    <p>{{ t('product.quicklinks') }}</p>

                                    <img
                                        :src="showSidebar ? sidebarClose : sidebarOpen"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>

                            <ul v-if="showSidebar">
                                <li>
                                    <a
                                        :href="'#' + t('product.tabs.information')"
                                        @click.prevent="(e) => handleTabsScroll((e.target as HTMLAnchorElement).hash)"
                                    >
                                        {{ t('product.tabs.information') }}
                                    </a>
                                </li>
                                <li v-if="hasProductItems">
                                    <a
                                        :href="'#' + t('product.tabs.package', sortedPZN.length)"
                                        @click.prevent="(e) => handleTabsScroll((e.target as HTMLAnchorElement).hash)"
                                    >
                                        {{ t('product.tabs.package', sortedPZN.length) }}
                                    </a>
                                </li>
                                <li v-if="data?.media?.downloads">
                                    <a
                                        :href="'#' + t('product.tabs.downloads')"
                                        @click.prevent="(e) => handleTabsScroll((e.target as HTMLAnchorElement).hash)"
                                    >
                                        {{ t('product.tabs.downloads') }}
                                    </a>
                                </li>
                                <li
                                    v-if="
                                        story?.content?.ConditionsAndCampaigns &&
                                        story?.content?.ConditionsAndCampaigns.length >= 1
                                    "
                                >
                                    <a href="#">{{ t('product.conditions') }}</a>
                                </li>
                                <li v-if="story?.content?.faq?.length && story.content.faq.length >= 1">
                                    <a
                                        :href="'#' + t('product.tabs.faq')"
                                        @click.prevent="(e) => handleTabsScroll((e.target as HTMLAnchorElement).hash)"
                                    >
                                        {{ t('product.tabs.faq') }}
                                    </a>
                                </li>
                                <li v-if="story?.content?.calculator?.[0]">
                                    <a :href="'#' + t('product.calculatorText')">
                                        {{ t('product.calculatorText') }}
                                    </a>
                                </li>
                                <li
                                    v-if="
                                        story?.content?.AssociatedNews?.length &&
                                        story.content.AssociatedNews.length > 0
                                    "
                                >
                                    <a :href="'#' + t('product.news')">
                                        {{ t('product.news') }}
                                    </a>
                                </li>
                                <li v-if="story?.content?.trainings?.length && story.content.trainings.length >= 1">
                                    <a
                                        :href="'#' + t('product.tabs.trainings')"
                                        @click.prevent="(e) => handleTabsScroll((e.target as HTMLAnchorElement).hash)"
                                    >
                                        {{ t('product.tabs.trainings') }}
                                    </a>
                                </li>
                                <li v-if="story?.content?.studies?.length && story.content.studies.length >= 1">
                                    <a
                                        :href="'#' + t('product.tabs.studies')"
                                        @click.prevent="(e) => handleTabsScroll((e.target as HTMLAnchorElement).hash)"
                                    >
                                        {{ t('product.tabs.studies') }}
                                    </a>
                                </li>
                                <li v-if="productMandatoryTextsEc || productMandatoryTextsHcp">
                                    <a :href="'#' + t('product.mandatoryText')">
                                        <template v-if="productMandatoryTextsEc">
                                            {{ t('product.mandatoryText') }}
                                        </template>
                                        <template v-else-if="productMandatoryTextsHcp">
                                            {{ t('product.mandatoryTextLoggedIn') }}
                                        </template>
                                    </a>
                                </li>
                                <li>
                                    <a
                                        :href="'#' + t('product.reportSideEffects')"
                                        @click.prevent="() => scrollToBottom()"
                                    >
                                        {{ t('product.reportSideEffects') }}
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <StoryblokComponentWrapper
                            v-if="story?.content?.brandWebsiteButton?.[0] && showSidebar"
                            :blok="story.content.brandWebsiteButton[0] as unknown as SbBlokData"
                            class="brand-button"
                        />
                    </div>
                </div>
                <div class="details">
                    <div
                        id="tabsWrapper"
                        ref="tabsWrapper"
                        class="tabs-wrapper"
                    >
                        <div
                            v-if="tabs.length >= 1"
                            class="tabs"
                        >
                            <PrimaryButton
                                v-for="(tab, index) in allTabs"
                                :key="index"
                                class="tab"
                                product-button
                                :button-text="tab.title"
                                variant="secondary"
                                :is-active="tab.title === activeTab"
                                :is-tab="true"
                                :is-locked="tab.isLocked"
                                @click="setActiveTab(tab)"
                            />
                        </div>
                        <div
                            id="tab-content"
                            class="tab-content"
                            :class="{ '--single': tabs.length === 1 }"
                        >
                            <!-- Optional -->

                            <div v-show="activeTab === t('product.tabs.information')">
                                <div
                                    v-if="data.features"
                                    class="table-container"
                                >
                                    <table class="table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    {{ t('product.tabs.tabContent.fachkreisInformationHeader') }}
                                                </th>
                                                <th>
                                                    {{ t('product.tabs.tabContent.fachkreisInformationDescription') }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-if="data.features.pharmacyOnly">
                                                <td>
                                                    {{ t('product.tabs.tabContent.pharmacy') }}
                                                </td>
                                                <td>
                                                    {{
                                                        data.features.pharmacyOnly?.value
                                                            ? t('general.yes')
                                                            : t('general.no')
                                                    }}
                                                </td>
                                            </tr>
                                            <tr v-if="data.features.prescriptionOnly">
                                                <td>
                                                    {{ t('product.tabs.tabContent.prescription') }}
                                                </td>
                                                <td>
                                                    {{
                                                        data.features.prescriptionOnly?.value
                                                            ? t('general.yes')
                                                            : t('general.no')
                                                    }}
                                                </td>
                                            </tr>
                                            <tr v-if="data.features.brand">
                                                <td>
                                                    {{ t('product.tabs.tabContent.brand') }}
                                                </td>
                                                <td>
                                                    {{
                                                        data.features.brand?.nameLocalized?.[lang] ??
                                                        data.features.brand?.name
                                                    }}
                                                </td>
                                            </tr>
                                            <tr v-if="data.features.dosageForm">
                                                <td>
                                                    {{ t('product.tabs.tabContent.dosage') }}
                                                </td>
                                                <td>
                                                    {{
                                                        data.features.dosageForm?.nameLocalized?.[lang] ??
                                                        data.features.dosageForm?.name
                                                    }}
                                                </td>
                                            </tr>

                                            <tr v-if="data.features.indication">
                                                <td>
                                                    {{ t('product.tabs.tabContent.indication') }}
                                                </td>
                                                <td>
                                                    {{
                                                        data.features.indication
                                                            .map(
                                                                (indication) =>
                                                                    indication.nameLocalized?.[lang] ?? indication.name
                                                            )
                                                            .join(', ')
                                                    }}
                                                </td>
                                            </tr>

                                            <tr v-if="data.features.applicationArea">
                                                <td>
                                                    {{
                                                        t(
                                                            'product.tabs.tabContent.applicationArea',
                                                            data.features.applicationArea.length
                                                        )
                                                    }}
                                                </td>
                                                <td>
                                                    {{
                                                        data.features.applicationArea
                                                            .map((area) => area.nameLocalized?.[lang] ?? area.name)
                                                            .join(', ')
                                                    }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Muss -->
                            <div
                                v-if="hasProductItems"
                                v-show="activeTab === t('product.tabs.package', sortedPZN.length)"
                                class="table-container"
                            >
                                <table class="table">
                                    <thead>
                                        <tr>
                                            <th>
                                                {{ t('product.tabs.tabContent.description') }}
                                            </th>
                                            <th>{{ t('product.tabs.tabContent.pzn') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="product in sortedPZN"
                                            :key="product.id"
                                        >
                                            <td>{{ product.nameLocalized?.[lang] ?? product.name }}</td>
                                            <td>{{ product.pzn ?? '' }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- optional -->
                            <div
                                v-show="activeTab === t('product.tabs.downloads')"
                                class="downloads"
                            >
                                <h2 class="tab-header">
                                    {{ t('product.tabs.tabContent.downloads') }}
                                </h2>
                                <div>
                                    <ul class="download-list">
                                        <li
                                            v-for="(download, index) in data?.media?.downloads"
                                            :key="index"
                                        >
                                            <DownloadLink
                                                :url="`/api/censhare/download/${download.id}`"
                                                :name="
                                                    download.nameLocalized?.[lang] ??
                                                    getDownloadName(download.type ?? '')
                                                "
                                                :lang="download.language?.toUpperCase() ?? undefined"
                                                :type="download.mimeType?.split('/').pop() as FileType"
                                            />
                                        </li>
                                        <li v-if="data.media && data.media.mainPicture?.[0]?.id">
                                            <DownloadLink
                                                :url="`${censhareEndpoint}/entity/pictures/${data.media.mainPicture[0].id}/storage/portal_web_high`"
                                                :name="
                                                    data.media.mainPicture?.[0]?.nameLocalized?.[lang] ??
                                                    data.media.specialistGroupGallery?.[0]?.nameLocalized?.[lang] ??
                                                    getDownloadName(data.media.mainPicture?.[0]?.type ?? '')
                                                "
                                                :lang="
                                                    data.media.mainPicture?.[0]?.language?.toUpperCase() ?? undefined
                                                "
                                                :type="'png'"
                                            />
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <!-- optional -->
                            <div v-if="campaignsTag && story?.content?.ConditionsAndCampaigns">
                                <div v-show="activeTab === campaignsTag && story?.content?.ConditionsAndCampaigns">
                                    <StoryblokComponentWrapper
                                        v-for="blok in bloks.conditionsAndCampaignsBloks"
                                        :key="blok._uid"
                                        :blok="blok as unknown as SbBlokData"
                                    />
                                </div>
                            </div>

                            <!-- optional -->
                            <div v-if="faqTag && story?.content?.faq">
                                <div v-show="activeTab === faqTag && story?.content?.faq">
                                    <StoryblokComponentWrapper
                                        v-if="bloks.faqBlok"
                                        :blok="bloks.faqBlok as unknown as SbBlokData"
                                    />
                                </div>
                            </div>

                            <!-- optional -->
                            <div v-if="trainingsTag && story?.content?.trainings">
                                <div v-show="activeTab === trainingsTag && story?.content?.trainings">
                                    <StoryblokComponentWrapper
                                        v-for="blok in bloks.trainingBloks"
                                        :key="(blok as Record<string, unknown>)._uid as string"
                                        :blok="blok as unknown as SbBlokData"
                                    />
                                </div>
                            </div>

                            <!-- optional -->
                            <div v-if="studiesTag && story?.content?.studies">
                                <div
                                    v-show="activeTab === studiesTag && story?.content?.studies"
                                    class="downloads"
                                >
                                    <h2 class="tab-header">
                                        {{ t('product.tabs.tabContent.studies') }}
                                    </h2>
                                    <StoryblokComponentWrapper
                                        v-for="blok in bloks.studyBloks"
                                        :key="blok._uid"
                                        :blok="blok as unknown as SbBlokData"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <LoginTeaser
                        v-if="!isLoggedInClient && story?.content"
                        :login-teaser-headline="story.content.loginTeaserHeadline"
                        :login-teaser-text="story.content.loginTeaserText"
                        :login-teaser-link="story.content.loginTeaserLink"
                        :removed-contents="story.removedContents"
                    />
                </div>

                <div
                    v-if="bloks.calculatorBlok"
                    :id="t('product.calculatorText')"
                >
                    <StoryblokComponentWrapper :blok="bloks.calculatorBlok as unknown as SbBlokData" />
                </div>
                <div
                    v-if="data.relations?.crossSellings"
                    class="crossselling-wrapper"
                >
                    <FachkreisCrosssellingSlider :product="data" />
                </div>

                <div
                    v-if="bloks.associatedNewsBlok"
                    :id="t('product.news')"
                    class="news"
                >
                    <h2>{{ t('product.news') }}</h2>
                    <StoryblokComponentWrapper
                        v-if="bloks.associatedNewsBlok"
                        :blok="bloks.associatedNewsBlok as unknown as SbBlokData"
                    />
                </div>

                <div v-if="bloks.additionalContent">
                    <StoryblokComponentWrapper :blok="bloks.additionalContent as unknown as SbBlokData" />
                </div>
                <div
                    :id="t('product.mandatoryText')"
                    class="legal"
                    data-testid="fachportal-pdp-legal-section"
                >
                    <ClientOnly>
                        <div
                            v-if="isLoggedInClient && productMandatoryTextsHcp && productMandatoryTextsHcp.length"
                            class="mandatory-text"
                            data-testid="fachportal-pdp-mandatory-text"
                        >
                            <div
                                v-for="text in productMandatoryTextsHcp"
                                :key="text.id"
                            >
                                <!-- eslint-disable vue/no-v-html -->
                                <div
                                    v-if="text.copytext"
                                    v-html="sanitizeHtml(parseXml(text.copytext))"
                                ></div>
                                <!-- eslint-enable vue/no-v-html -->
                            </div>
                        </div>

                        <div
                            v-if="!isLoggedInClient && productMandatoryTextsEc && productMandatoryTextsEc.length"
                            class="mandatory-text"
                            data-testid="fachportal-pdp-mandatory-text"
                        >
                            <div
                                v-for="text in productMandatoryTextsEc"
                                :key="text.id"
                            >
                                <!-- eslint-disable vue/no-v-html -->
                                <div
                                    v-if="text.copytext"
                                    v-html="sanitizeHtml(parseXml(text.copytext))"
                                ></div>
                                <!-- eslint-enable vue/no-v-html -->
                            </div>
                        </div>
                    </ClientOnly>

                    <ul
                        v-if="allFootnotes.length >= 1"
                        class="footnotes"
                    >
                        <li
                            v-for="entry in allFootnotes"
                            :id="`footnote-${entry.number}`"
                            :key="`note-${entry.number}`"
                        >
                            <sup class="footnote-label">{{ entry.number }}</sup>
                            {{ entry.text }}
                            <a
                                href="#"
                                class="back-to-text"
                            >
                                ↑
                            </a>
                        </li>
                    </ul>
                </div>
            </template>
        </div>
    </div>

    <SharedLoginModal :login-modal-controller="loginModalController" />
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/detail-page/front-end-detail-page.scss';
</style>
